// Diagnostics of the network test's UDP echo (pure, no DOM; unit-tested in Node). See SPEC.md
// "nettest.html" phase b (udp.diag). Reads RTCStats lists; never keeps an address, only
// families, candidate types and counts.
import { candidateFamily } from './netfamily.js';

// The step that did not complete, in the order the echo needs them (send and recv side by
// side). null = the channel opened.
export const FAIL_STAGES = Object.freeze([
  'net_open', // net.open: no reply, or an error reply
  'net_setup', // net.open answered, the browser could not set up the device or the transports
  'send_connect', // send transport: transport.connect (our DTLS parameters) refused or unanswered
  'recv_connect', // recv transport: the same
  'send_ice', // send transport: no ICE pair succeeded
  'recv_ice', // recv transport: no ICE pair succeeded
  'send_dtls', // send transport: ICE up, DTLS handshake not completed
  'recv_dtls', // recv transport: ICE up, DTLS handshake not completed
  'dp_open', // data producer (SCTP on send) not open, or produceData failed
  'dc_open', // data consumer (SCTP on recv) not open, or consumeData failed
]);

// Short MSA names of each step, for the results page («أسباب تعذّر UDP: …»).
export const FAIL_STAGE_LABELS = Object.freeze({
  net_open: 'تعذّر طلب فتح القناة',
  net_setup: 'تعذّر التجهيز في المتصفّح',
  send_connect: 'لم يكتمل طلب الاتّصال (الإرسال)',
  recv_connect: 'لم يكتمل طلب الاتّصال (الاستقبال)',
  send_ice: 'لم يكتمل الاتّصال (الإرسال)',
  recv_ice: 'لم يكتمل الاتّصال (الاستقبال)',
  send_dtls: 'لم تكتمل المصافحة الآمنة (الإرسال)',
  recv_dtls: 'لم تكتمل المصافحة الآمنة (الاستقبال)',
  dp_open: 'لم تُفتح قناة الإرسال',
  dc_open: 'لم تُفتح قناة الاستقبال',
});

// One plain line for the nettest page when the channel did not open. `diag` (optional, the
// run's udp.diag) refines it: net_open says "no reply" only for a timeout, and dp_open/dc_open
// say the connection completed only when that transport really was connected.
export function failStageText(stage, diag = null) {
  const pre = 'تعذّر فتح قناة القياس: ';
  const err = diag && diag.err;
  switch (stage) {
    case 'net_open':
      if (err === 'timeout') return `${pre}لم يردّ الخادم على طلب فتح القناة.`;
      return err ? `${pre}ردّ الخادم بخطأ على طلب فتح القناة.` : `${pre}لم يكتمل طلب فتح القناة.`;
    case 'net_setup': return `${pre}ردّ الخادم ولم يتمكّن المتصفّح من تجهيز القناة.`;
    case 'send_connect': return `${pre}لم يكتمل طلب بدء الاتّصال مع الخادم (مسار الإرسال).`;
    case 'recv_connect': return `${pre}لم يكتمل طلب بدء الاتّصال مع الخادم (مسار الاستقبال).`;
    case 'send_ice': return `${pre}لم يكتمل الاتّصال بالخادم (مسار الإرسال).`;
    case 'recv_ice': return `${pre}لم يكتمل الاتّصال بالخادم (مسار الاستقبال).`;
    case 'send_dtls': return `${pre}اكتمل الاتّصال ولم تكتمل المصافحة الآمنة (مسار الإرسال).`;
    case 'recv_dtls': return `${pre}اكتمل الاتّصال ولم تكتمل المصافحة الآمنة (مسار الاستقبال).`;
    case 'dp_open':
      return diag && diag.sendState === 'connected' ? `${pre}اكتمل الاتّصال ولم تُفتح قناة الإرسال.` : `${pre}لم تُفتح قناة الإرسال.`;
    case 'dc_open':
      return diag && diag.recvState === 'connected' ? `${pre}اكتمل الاتّصال ولم تُفتح قناة الاستقبال.` : `${pre}لم تُفتح قناة الاستقبال.`;
    default: return `${pre}سبب غير معروف.`;
  }
}

// mediasoup-client transport connectionState values (RTCPeerConnection.connectionState).
export const CONN_STATES = Object.freeze(['new', 'connecting', 'connected', 'disconnected', 'failed', 'closed']);
export const CHANNEL_STATES = Object.freeze(['connecting', 'open', 'closing', 'closed']);
export const ICE_STATES = Object.freeze(['new', 'checking', 'connected', 'completed', 'disconnected', 'failed', 'closed']);
export const DTLS_STATES = Object.freeze(['new', 'connecting', 'connected', 'closed', 'failed']);
export const CAND_TYPES = Object.freeze(['host', 'srflx', 'prflx', 'relay']);

// Stage names of the timeline (ms since net.open was sent). `<dir><State>` = first time the
// transport's connectionState became that state.
export const STAGE_NAMES = Object.freeze([
  'netOpen', // net.open answered
  'sendConnect', 'recvConnect', // the transport's 'connect' event: DTLS parameters sent to the server
  'sendConnectOk', 'recvConnectOk', // transport.connect answered
  'produced', // net.produceData answered (server DataProducer + echo ready)
  'consumed', // recv.consumeData resolved
  ...['send', 'recv'].flatMap((d) => CONN_STATES.map((s) => d + s[0].toUpperCase() + s.slice(1))),
  'sendGathered', 'recvGathered', // ICE gathering complete
  // Data producer / consumer first seen open (SCTP association up): an upper bound. The
  // producer's channel can open during the net.produceData round trip, before the page holds
  // it; dpOpen is then the moment produceData resolved.
  'dpOpen', 'dcOpen',
  'end', // diagnostics taken (success: after the echo; failure: when giving up)
]);

// Codes of exceptions: our own coded errors ('timeout', 'bad_request', 'closed', ...) or an
// error class name ('InvalidStateError'). Never a message text (it could quote SDP).
export function errCode(e) {
  const c = e && (e.code || e.message);
  if (typeof c === 'string' && /^[a-z][a-z0-9_]{0,39}$/.test(c)) return c;
  const n = e && e.name;
  if (typeof n === 'string' && /^[A-Za-z][A-Za-z0-9_]{0,39}$/.test(n)) return n;
  return 'error';
}

export function statsList(report) {
  const list = [];
  if (!report) return list;
  if (typeof report.forEach === 'function') report.forEach((s) => list.push(s));
  else if (typeof report === 'object') for (const k of Object.keys(report)) list.push(report[k]);
  return list.filter((s) => s && typeof s === 'object');
}

const count = (x) => (typeof x === 'number' && Number.isFinite(x) && x > 0 ? Math.round(x) : 0);
const protoOf = (...cands) => {
  for (const c of cands) {
    const p = String((c && c.protocol) || '').toLowerCase();
    if (p === 'udp' || p === 'tcp') return p;
  }
  return null;
};

// ---------- stats log: getStats() samples of one transport, merged ----------
// Chrome deletes a candidate pair (and its remote candidate) from getStats() once the pair has
// failed: pairs whose checks time out while others still try, and every pair as soon as the
// transport's connectionState is 'failed' (seen in Chrome 153). A single report taken when
// giving up would then read "no ICE check was ever sent". So the page samples getStats() about
// once a second until the channel opens or the wait ends, and merges the samples here: per pair
// id the highest requestsSent / responsesReceived, whether it ever succeeded, and its protocol
// and family as seen while its candidates were still listed; the local candidates by id; the
// last selected pair; the last ICE/DTLS states. Counts and enums only, never an address.
const LOG_MAX_PAIRS = 64;
const LOG_MAX_CANDS = 32;

export function newStatsLog() {
  return { pairs: new Map(), locals: new Map(), selected: null, ice: null, dtls: null };
}

// The transport's selected pair in one report (no address): { protocol, family, localType,
// remoteType, rttMs }, or null when none is selected yet (or the browser no longer lists it).
export function selectedPairOf(list) {
  const byId = new Map(list.filter((s) => s.id).map((s) => [s.id, s]));
  let pair = null;
  for (const s of list) {
    if (s.type === 'transport' && s.selectedCandidatePairId && byId.has(s.selectedCandidatePairId)) {
      pair = byId.get(s.selectedCandidatePairId);
      break;
    }
  }
  const pairs = list.filter((s) => s.type === 'candidate-pair');
  pair = pair || pairs.find((p) => p.selected === true) || pairs.find((p) => p.nominated && p.state === 'succeeded') || null;
  if (!pair) return null;
  const local = byId.get(pair.localCandidateId) || null;
  const remote = byId.get(pair.remoteCandidateId) || null;
  const protocol = protoOf(local, remote);
  if (!protocol) return null;
  const rtt = pair.currentRoundTripTime;
  return {
    protocol,
    family: candidateFamily(remote) ?? candidateFamily(local),
    localType: local && CAND_TYPES.includes(local.candidateType) ? local.candidateType : null,
    remoteType: remote && CAND_TYPES.includes(remote.candidateType) ? remote.candidateType : null,
    rttMs: typeof rtt === 'number' && Number.isFinite(rtt) && rtt >= 0 ? rtt * 1000 : null,
  };
}

// Merges one getStats() list into `log` (see above). Idempotent for the same list. Returns log.
export function recordStats(log, list) {
  const byId = new Map(list.filter((s) => s.id).map((s) => [s.id, s]));
  for (const s of list) {
    if (s.type === 'transport') {
      if (ICE_STATES.includes(s.iceState)) log.ice = s.iceState;
      if (DTLS_STATES.includes(s.dtlsState)) log.dtls = s.dtlsState;
    } else if (s.type === 'local-candidate' && s.id) {
      if (!log.locals.has(s.id) && log.locals.size >= LOG_MAX_CANDS) continue;
      log.locals.set(s.id, {
        fam: candidateFamily(s), type: CAND_TYPES.includes(s.candidateType) ? s.candidateType : null, proto: protoOf(s),
      });
    } else if (s.type === 'candidate-pair' && s.id) {
      const prev = log.pairs.get(s.id);
      if (!prev && log.pairs.size >= LOG_MAX_PAIRS) continue;
      const local = byId.get(s.localCandidateId) || null;
      const remote = byId.get(s.remoteCandidateId) || null;
      log.pairs.set(s.id, {
        proto: protoOf(local, remote) ?? (prev ? prev.proto : null),
        fam: candidateFamily(remote) ?? candidateFamily(local) ?? (prev ? prev.fam : null),
        req: Math.max(prev ? prev.req : 0, count(s.requestsSent)),
        resp: Math.max(prev ? prev.resp : 0, count(s.responsesReceived)),
        ok: Boolean(prev && prev.ok) || s.state === 'succeeded',
      });
    }
  }
  const sel = selectedPairOf(list);
  if (sel) log.selected = sel;
  return log;
}

// ICE/DTLS state of one transport: the RTCTransportStats iceState/dtlsState of the final report
// (when the browser has them; else the last sampled), the candidate pairs seen during the run and
// how many ever succeeded, and the ICE checks sent / answered over UDP per family of the remote
// candidate (our announced address). `list` = the final report; `log` = the run's samples (the
// final report is merged into it first); without a log, the final report alone.
export function iceSummary(list, log = null) {
  const merged = recordStats(log || newStatsLog(), list);
  const out = {
    ice: merged.ice, dtls: merged.dtls, pairs: 0, ok: 0, tcpOk: 0, req4: 0, resp4: 0, req6: 0, resp6: 0,
  };
  for (const p of merged.pairs.values()) {
    out.pairs++;
    if (p.ok) {
      out.ok++;
      if (p.proto === 'tcp') out.tcpOk++;
    }
    if (p.proto === 'udp' && p.fam) {
      const k = p.fam === 'ipv6' ? '6' : '4';
      out[`req${k}`] += p.req;
      out[`resp${k}`] += p.resp;
    }
  }
  return out;
}

// Local candidates the browser gathered (in the final report, and in the samples of `log` when
// given), counted by family (hidden = mDNS name or no address), type and protocol. No addresses.
export function localCandCounts(list, log = null) {
  const merged = recordStats(log || newStatsLog(), list);
  const out = { ipv4: 0, ipv6: 0, hidden: 0, host: 0, srflx: 0, prflx: 0, relay: 0, udp: 0, tcp: 0 };
  for (const c of merged.locals.values()) {
    if (c.fam) out[c.fam]++;
    else out.hidden++;
    if (c.type) out[c.type]++;
    if (c.proto) out[c.proto]++;
  }
  return out;
}

// A transport that is not connected: ICE never got a pair through → '<dir>_ice'; ICE did but
// DTLS did not finish → '<dir>_dtls'. null when it is connected (or DTLS says so already).
function transportStage(dir, side) {
  if (!side || side.state === 'connected') return null;
  const ice = side.ice || {};
  if (ice.dtls === 'connected') return null;
  const iceOk = ice.ice === 'connected' || ice.ice === 'completed' || ice.ok > 0;
  return iceOk ? `${dir}_dtls` : `${dir}_ice`;
}

// produceData / consumeData threw. mediasoup-client sends transport.connect (our DTLS
// parameters) inside that call and starts ICE only once it is answered: connect sent and never
// answered OK → '<dir>_connect'. A transport that already failed → its ICE/DTLS step. Else
// null: the call itself failed (dp_open / dc_open, worded neutrally).
function callStage(dir, side, stages) {
  if (stages[`${dir}Connect`] !== undefined && stages[`${dir}ConnectOk`] === undefined) return `${dir}_connect`;
  if (side && (side.state === 'failed' || side.state === 'closed')) return transportStage(dir, side);
  return null;
}

// The first step that did not complete. `step` = where the run was: 'net_open' (waiting for
// the net.open reply), 'net_setup' (device and transports), 'produce' (send.produceData),
// 'consume' (recv.consumeData), 'open' (waiting for both ends to open), 'done' (opened).
// `stages` = the timeline (ctx.stages). `failedDir` = 'send'|'recv' when the wait ended because
// that transport failed: its step is named, never a side that was merely still connecting.
// Otherwise (open_timeout) the transports' states say what is missing, send side first (the
// echo goes out on send and back on recv).
// send/recv: { state: connectionState, ice: iceSummary() }.
export function failStageOf({ step, send, recv, dpState, dcState, stages = {}, failedDir = null }) {
  const st = stages && typeof stages === 'object' ? stages : {};
  if (step === 'done') return null;
  if (step === 'net_open' || !step) return 'net_open';
  if (step === 'net_setup') return 'net_setup';
  if (step === 'produce') return callStage('send', send, st) ?? 'dp_open';
  if (step === 'consume') return callStage('recv', recv, st) ?? 'dc_open';
  if (failedDir === 'send') return transportStage('send', send) ?? 'dp_open';
  if (failedDir === 'recv') return transportStage('recv', recv) ?? 'dc_open';
  if (dpState !== 'open') return transportStage('send', send) ?? 'dp_open';
  if (dcState !== 'open') return transportStage('recv', recv) ?? 'dc_open';
  return null;
}
